<template>
    <div>
        <v-row
            v-for="(contact, index) in client.contacts"
            :key="contact.id"
            no-gutters
            :class="['pa-0 mt-2', { 'mb-6': index !== client.contacts.length - 1 }]"
        >
            <v-col>
                <v-row no-gutters style="flex-wrap: nowrap">
                    <v-avatar color="neutral_n40" size="24" class="mr-2">
                        <span class="caption neutral_n300--text">
                            {{ contact | format_initials }}
                        </span>
                    </v-avatar>
                    <div class="d-flex flex-column">
                        <span class="body-2 font-weight-medium">
                            {{ contact | format_name }}
                            <v-icon v-if="contact.is_primary" class="ml-1" color="secondary" :size="20">
                                mdi-star
                            </v-icon>
                            <small v-if="contact.job_title" class="grey--text">
                                &nbsp;&nbsp;{{ contact.job_title }}
                            </small>
                        </span>
                        <span class="body-2 grey--text">
                            {{ contact.roles.map(x => getRoleName(x)).filter(x => x).join(", ") }}
                        </span>
                    </div>
                </v-row>

                <div
                    v-for="(customField, fieldIndex) in preparedContactCustomFields(contact)"
                    :key="fieldIndex"
                >
                    <v-row
                        no-gutters
                        class="body-2 mt-2 ml-1 align-center"
                    >
                        <LinkField
                            v-if="customField.field.type === 'url' "
                            :field="customField"
                        />
                        <BasicCustomField
                            v-else
                            :field="customField"
                            :related-object="client"
                            :icon="getIcon(customField.field.name)"
                            :showValueTitle="customField.field.data_column === 'contact.fields'"
                        />
                    </v-row>
                </div>
                <div>
                    <v-row
                        no-gutters
                        class="body-2 mt-2 ml-1 align-center"
                    >
                        <CpInviteField
                            :contact="contact"
                            :client="client"
                        />
                    </v-row>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import AppModule from "../../../store/modules/app";
import CustomFieldsModule from "@/store/modules/custom_fields";
import {formatCustomFieldValue} from "../../../composables/use-custom-fields";
import {computed, ref} from "@vue/composition-api";
import customField from "../../CustomField.vue";
import BasicCustomField from "./BasicCustomField.vue";
import LinkField from "./LinkField.vue";
import CpInviteField from "@/components/Sidebar/Fields/CpInviteField.vue";

export default {
    name: "ClientContactsList",
    computed: {
        customField() {
            return customField
        }
    },
    components: {CpInviteField, LinkField, BasicCustomField},
    props: {
        client: {
            type: Object,
            required: true
        },
        showSystemFields: {
            type: Boolean,
            default: true
        },
    },
    setup(props) {
        const {roles} = AppModule();
        const {getCustomFields} = CustomFieldsModule();
        const sidebarValues = ref(null);
        const allowedSystemCustomFields = ["mobile_phone", "work_phone", "email", "birthday", "locale_code"]

        const isOverflowing = () => {
            if (sidebarValues.value) {
                return sidebarValues.value.scrollWidth > sidebarValues.value.clientWidth;
            }
            return false;
        };

        const getIcon = (name) => {
            const iconNameMapper = {
                mobile_phone: "Phone",
                work_phone: "Phone",
                email: "Mail",
                birthday: "CalendarDays",
            };
            const iconName = iconNameMapper[name] || "Circle";

            return {
                name: iconName,
                color: "g4"
            }
        }

        const contactCustomFields = computed(() => {
            return getCustomFields(props.client.account_id, 'contact');
        })

        const preparedContactCustomFields = (contact) => {
            const customFields = contactCustomFields.value

            const preparedCustomfields = []
            for (const customField of customFields) {
                if (isCustomFieldAllowed(customField, contact)) {
                    const preparedField = {
                        field: customField,
                        value: contact[customField.name]
                    }
                    preparedCustomfields.push(preparedField)
                }
            }
            return preparedCustomfields
        }

        const isCustomFieldAllowed = (customField, contact) => {
            const hasValue = contact[customField.name]
            const isCustomerDefinedField = customField.data_column === "contact.fields"
            const isSystemCustomFieldAllowed = allowedSystemCustomFields.includes(customField.name)

            return hasValue && (isCustomerDefinedField || isSystemCustomFieldAllowed)
        }

        const getRoleName = roleId => {
            if (roles.value[props.client.account_id] === undefined) return "";
            let role = roles.value[props.client.account_id].find(x => x.id === roleId);
            return role == null ? null : role.name;
        }

        return {
            getRoleName,
            formatCustomFieldValue,
            isOverflowing,
            sidebarValues,
            contactCustomFields,
            getIcon,
            preparedContactCustomFields,
        };
    }
};
</script>
<style scoped lang="scss">
.text-wrapping {
    white-space: nowrap;
}
</style>